import {
  Eye,
  EyeOff,
  FileBarChart,
  FolderOpen,
  KeySquare,
  ListTree,
  Lock,
  LockOpen,
  Loader2,
  Maximize2,
  Minimize2,
  MonitorPlay,
  Power,
  PowerOff,
  RotateCcw,
  TerminalSquare,
  Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useParams } from 'react-router';
import { PulpitMaszyny, type SterowanieSesja } from '@/components/PulpitMaszyny';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import {
  sterujPulpitem,
  wyslijCtrlAltDel,
  uruchomSkrot,
  uruchomSpsui,
  zlecZasilanie,
  type SkrotMaszyny,
  type ZasilanieMaszyny,
} from '@/lib/machines-api';
import { wyslijPlikNaMaszyne } from '@/lib/wysylka-pliku';
import { rozmiarOknaPulpitu } from './rozmiar-okna-pulpitu';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PULPIT W OSOBNYM OKNIE PRZEGLĄDARKI.
 *
 *  ⚠️⚠️ ZGŁOSZONE 15.09.2026: „okienko nowe jest fajne i ruchome, ale nadal przejście na inną
 *  stronę panelu je zupełnie wyłącza — raczej to powinno być w jakimś osobnym okienku". Pływające
 *  okno wewnątrz ekranu automatów ginęło razem z tamtą trasą, bo było jej częścią. Osobne okno
 *  przeżywa nawigację, przeżywa odświeżenie portalu, daje się położyć na drugim monitorze
 *  i zamyka się zwykłym krzyżykiem.
 *
 *  ⚠️⚠️ CAŁA WYSOKOŚĆ NALEŻY DO OBRAZU. Trzy zgłoszenia tego samego dnia: „ten dolny biały pasek
 *  też wywal, szkoda miejsca w pionie", „górny biały pasek też wywal", „mogłoby też robić w pionie
 *  resize do rozdzielczości maszyny". Wszystkie mówią o jednym: ekrany automatów bywają STOJĄCE
 *  (PESA-3 melduje 1080×1920), a każdy poziomy pasek zabiera dokładnie ten wymiar, którego brakuje.
 *  Nazwa automatu poszła więc w TYTUŁ OKNA, komunikaty leżą na obrazie, a skróty stoją PIONOWO
 *  z prawej — bo tam kosztują szerokość, której jest w nadmiarze.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Szerokość paska skrótów — wchodzi też do rachunku rozmiaru okna, więc stoi w JEDNYM miejscu. */
const PASEK_PX = 66;

export default function PulpitOkno() {
  const { t } = useI18n();
  const { machineId } = useParams<{ machineId: string }>();
  const [parametry] = useSearchParams();
  const groupId = parametry.get('groupId');
  const nazwaZAdresu = parametry.get('nazwa');

  const [nazwa, setNazwa] = useState<string | null>(nazwaZAdresu);
  const [pelnyEkran, setPelnyEkran] = useState(false);
  const [wToku, setWToku] = useState<string | null>(null);
  const [nota, setNota] = useState<string | null>(null);
  const [wysylka, setWysylka] = useState<{ nazwa: string; procent: number } | null>(null);
  const [sterowanie, setSterowanie] = useState<SterowanieSesja | null>(null);
  /** Stan z ODPOWIEDZI serwera — nie z kliknięcia; konserwacja wymusza blokadę. */
  const [blokada, setBlokada] = useState(false);
  const [konserwacja, setKonserwacja] = useState(false);
  /** Która pozycja zasilania czeka na potwierdzenie w oknie „czy na pewno". */
  const [pytanie, setPytanie] = useState<ZasilanieMaszyny | null>(null);
  /** Czy lista pozycji systemowych jest rozwinięta. */
  const [menuSystem, setMenuSystem] = useState(false);
  /** Czy lista programów SPS-a jest rozwinięta. */
  const [menuSps, setMenuSps] = useState(false);
  /**
   * ⚠️⚠️ WYŁĄCZENIE WYMAGA JESZCZE JEDNEGO KLIKNIĘCIA W „TAK" — zgłoszone 16.09.2026. Po restarcie
   * maszyna wraca sama; po wyłączeniu NIE WRÓCI, dopóki ktoś nie pojedzie i nie wciśnie przycisku.
   * To nie są dwie równorzędne decyzje i okno nie ma prawa traktować ich tak samo.
   */
  const [drugiKlik, setDrugiKlik] = useState(false);
  const dopasowano = useRef(false);
  /**
   * ⚠️ NASŁUCH `drop` REJESTRUJEMY RAZ, więc nie może domykać świeżego stanu. Ref trzyma aktualną
   * funkcję wysyłki; bez tego upuszczenie pliku wołałoby wersję z pierwszego renderu, która nie
   * zna ani nazwy maszyny, ani tego, że inna wysyłka właśnie trwa.
   */
  const wyslij = useRef<((plik: File) => void) | null>(null);

  /**
   * ⚠️⚠️ NAZWA AUTOMATU IDZIE W TYTUŁ OKNA — zgłoszone 15.09.2026: „nazwę instancji daj na nazwę
   * paska u góry jak da radę, w tej chwili na górnym pasku jest po prostu VENDI". Przy kilku
   * otwartych pulpitach naraz pasek zadań i tytuł okna to jedyne miejsca, po których da się je
   * rozróżnić — a my właśnie usunęliśmy nagłówek, który tę nazwę nosił.
   */
  useEffect(() => {
    const poprzedni = document.title;
    document.title = nazwa === null ? t('pulpit.tytul') : `${t('pulpit.tytul')} — ${nazwa}`;
    return () => {
      document.title = poprzedni;
    };
  }, [nazwa, t]);

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  UPUSZCZENIE PLIKU WYSYŁA GO NA MASZYNĘ — do `C:\sdi\przeslane`.
   *
   *  ⚠️⚠️ NAJPIERW MUSIAŁO PRZESTAĆ ZABIJAĆ SESJĘ (zgłoszone 15.09.2026: „przeniesienie pliku na
   *  ten ekran drag&drop od razu zamyka sesję"). To nie była awaria pulpitu, tylko DOMYŚLNE
   *  ZACHOWANIE PRZEGLĄDARKI: strona, która nie odwoła zdarzenia `drop`, każe oknu PRZEJŚĆ POD
   *  ADRES upuszczonego pliku. Okno nawigowało na `file:///…`, gniazdo padało, a instancja gasiła
   *  serwer na maszynie — z zewnątrz wyglądało to na tunel zerwany przez plik. `preventDefault`
   *  na obu zdarzeniach zostaje więc tutaj NA ZAWSZE, niezależnie od tego, co robimy z plikiem.
   *
   *  ⚠️⚠️ PLIK NIE IDZIE PRZEZ RFB, BO RFB PLIKÓW NIE WIEZIE (przesyłanie w TightVNC i UltraVNC to
   *  ich własne rozszerzenia, których noVNC nie zna). Jedzie NASZYM kanałem: przeglądarka →
   *  instancja → agent, częściami, dokładnie tak jak kopie w drugą stronę.
   *
   *  ⚠️ NASŁUCH NA `window`, NIE NA OBRAZIE. Nawiguje OKNO, więc upuszczenie obok płótna robiło
   *  dokładnie to samo — pilnowanie samego płótna zostawiłoby dziurę tam, gdzie najłatwiej chybić.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  useEffect(() => {
    const trzymaj = (e: DragEvent): void => {
      e.preventDefault();
      if (e.dataTransfer !== null) e.dataTransfer.dropEffect = 'copy';
    };
    const upusc = (e: DragEvent): void => {
      e.preventDefault();
      const pliki = e.dataTransfer?.files;
      if (pliki === undefined || pliki.length === 0) return;
      /**
       * ⚠️ JEDEN PLIK NA RAZ. Kilka plików naraz przez łącze automatu to kilka wysyłek w tle,
       * z których żadna nie ma swojego paska — a przy 500 MB każda trwa minuty.
       */
      wyslij.current?.(pliki[0]!);
    };
    window.addEventListener('dragover', trzymaj);
    window.addEventListener('drop', upusc);
    return () => {
      window.removeEventListener('dragover', trzymaj);
      window.removeEventListener('drop', upusc);
    };
  }, []);

  /** ⚠️ Każde otwarcie i zamknięcie okna zaczyna liczenie od nowa — inaczej poprzednie „Tak" zostawałoby zaliczone. */
  useEffect(() => {
    setDrugiKlik(false);
  }, [pytanie]);

  /** ⚠️ Zdanie znika samo — to komunikat o jednym kliknięciu, a nie stan okna. */
  useEffect(() => {
    if (nota === null) return;
    const zegar = setTimeout(() => setNota(null), 8000);
    return () => clearTimeout(zegar);
  }, [nota]);

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  CTRL+V MA DZIAŁAĆ TAK, JAK WSZĘDZIE — bez pola, bez przycisku (zgłoszone 16.09.2026).
   *
   *  ⚠️⚠️ PRZECHWYTUJEMY W FAZIE PRZECHWYTYWANIA, NA `window`, czyli ZANIM noVNC zobaczy klawisz.
   *  noVNC woła na każdym wciśnięciu `preventDefault()` i `stopPropagation()`, więc gdybyśmy
   *  słuchali normalnie, Ctrl+V nigdy by do nas nie dotarło. `stopPropagation` po naszej stronie
   *  zabiera mu ten klawisz — i dobrze, bo wysyłamy go sami, ale DOPIERO po ustawieniu schowka.
   *
   *  ⚠️⚠️ TREŚĆ BIERZEMY ZE ZDARZENIA `paste`, A NIE Z `navigator.clipboard.readText()`. Zgłoszone
   *  16.09.2026: „niby coś wkleja, ale wyświetla niepotrzebne okienko". Tym okienkiem był monit
   *  o UPRAWNIENIE do schowka, którego `readText` wymaga — i pokazywał się przy każdym wklejeniu.
   *  `ClipboardEvent` niesie treść od ręki, bo to użytkownik właśnie nacisnął Ctrl+V.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  useEffect(() => {
    /**
     * ⚠️⚠️ NIE WOŁAMY TU `preventDefault()` — I TO JEST CAŁA RÓŻNICA. Zabranie klawiszowi domyślnej
     * akcji kasuje zdarzenie `paste`, a to właśnie ono niesie treść schowka BEZ PYTANIA O ZGODĘ.
     * Zostaje samo `stopPropagation`, żeby noVNC nie dostało klawisza i nie zrobiło
     * `preventDefault()` za nas — Ctrl+V na maszynę wysyłamy sami, w obsłudze `paste`.
     */
    const naKlawisz = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey && !e.altKey && (e.key === 'v' || e.key === 'V'))) return;
      if (sterowanie === null) return;
      e.stopPropagation();
    };

    /**
     * ⚠️⚠️ TREŚĆ BIERZEMY ZE ZDARZENIA, A NIE Z `navigator.clipboard.readText()`. To jest naprawa
     * zgłoszenia „niby coś wkleja, ale wyświetla niepotrzebne okienko" (16.09.2026): `readText`
     * to API wymagające UPRAWNIENIA, więc przeglądarka pyta o nie monitem — przy każdym wklejeniu.
     * `ClipboardEvent.clipboardData` jest dostępne od ręki, bo to użytkownik właśnie nacisnął
     * Ctrl+V; żadnego monitu nie ma i być nie może.
     *
     * ⚠️ KOLEJNOŚĆ DALEJ JEST TREŚCIĄ: najpierw treść na maszynę, potem dopiero Ctrl+V. Odwrotnie
     * program na automacie wkleiłby to, co leżało w JEGO schowku wcześniej — czyli cudzą treść.
     * Oba idą tym samym strumieniem, a pomocnik przetwarza je po kolei, więc kolejność się trzyma.
     */
    const naWklejenie = (e: ClipboardEvent): void => {
      const s = sterowanie;
      if (s === null) return;
      const tekst = e.clipboardData?.getData('text/plain') ?? '';
      e.preventDefault();
      if (tekst !== '') s.wyslijSchowek(tekst);
      s.wklej();
    };

    window.addEventListener('keydown', naKlawisz, true);
    window.addEventListener('paste', naWklejenie, true);
    return () => {
      window.removeEventListener('keydown', naKlawisz, true);
      window.removeEventListener('paste', naWklejenie, true);
    };
  }, [sterowanie]);

  /** Pełny ekran bywa też włączany klawiszem F11 — stan czytamy z przeglądarki, nie z własnej pamięci. */
  useEffect(() => {
    const zmiana = (): void => setPelnyEkran(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', zmiana);
    return () => document.removeEventListener('fullscreenchange', zmiana);
  }, []);

  /**
   * ⚠️⚠️ PASKA ADRESU NIE DA SIĘ ZDJĄĆ Z WYSKAKUJĄCEGO OKNA — zgłoszone 15.09.2026: „w wyskakującym
   * okienku wywal pasek adresu, jest zbędny". `window.open('…','…','location=no')` przestało
   * działać lata temu i to jest ŚWIADOMA decyzja przeglądarek: okno bez widocznego adresu można
   * podstawić pod dowolną stronę. Jedyne, co naprawdę zdejmuje całą ramkę razem z paskiem, to
   * pełny ekran — i dlatego ten przycisk tu jest, zamiast obietnicy, której nie dowieziemy.
   */
  const przelaczPelnyEkran = useCallback((): void => {
    void (async () => {
      try {
        if (document.fullscreenElement === null) await document.documentElement.requestFullscreen();
        else await document.exitFullscreen();
      } catch {
        setNota(t('pulpit.pelnyEkranOdmowa'));
      }
    })();
  }, [t]);

  /**
   * ⚠️⚠️ DOPASOWANIE RAZ, PRZY PIERWSZYM OBRAZIE. Powtarzanie przy każdym zdarzeniu odbierałoby
   * człowiekowi prawo do ustawienia okna po swojemu — a rozdzielczość maszyny w trakcie sesji
   * się nie zmienia.
   *
   * ⚠️ `resizeTo` DZIAŁA WYŁĄCZNIE NA OKNACH OTWARTYCH SKRYPTEM. Tę trasę da się też wkleić do
   * zwykłej karty i wtedy przeglądarka po prostu odmówi — stąd `try`, a nie komunikat o błędzie.
   */
  const dopasujOkno = useCallback((maszyna: { szerokosc: number; wysokosc: number }): void => {
    if (dopasowano.current) return;
    dopasowano.current = true;
    if (window.opener === null) return;

    const docelowy = rozmiarOknaPulpitu(
      maszyna,
      { szerokosc: window.screen.availWidth, wysokosc: window.screen.availHeight },
      {
        szerokosc: Math.max(0, window.outerWidth - window.innerWidth),
        wysokosc: Math.max(0, window.outerHeight - window.innerHeight),
      },
      PASEK_PX,
    );
    if (docelowy === null) return;
    try {
      window.resizeTo(docelowy.szerokosc, docelowy.wysokosc);
    } catch {
      /* przeglądarka ma prawo odmówić — obraz i tak się skaluje */
    }
  }, []);

  const zlec = useCallback(
    (klucz: string, akcja: () => Promise<unknown>): void => {
      if (wToku !== null) return;
      setWToku(klucz);
      setNota(null);
      void akcja()
        .then(() => setNota(t('pulpit.zlecone')))
        .catch((e: unknown) => setNota(e instanceof ApiError ? e.message : t('pulpit.nieUdaloSie')))
        .finally(() => setWToku(null));
    },
    [wToku, t],
  );

  /**
   * ⚠️⚠️ WYSYŁKA W REFIE, NIE W ZALEŻNOŚCIACH NASŁUCHU. Rejestrowanie `drop` od nowa przy każdej
   * zmianie postępu znaczyłoby dziesiątki wpięć i wypięć w trakcie jednej wysyłki — a między nimi
   * okno przez moment nie ma nasłuchu i upuszczony wtedy plik wraca do DOMYŚLNEGO zachowania
   * przeglądarki, czyli nawiguje okno i zabija sesję. Dokładnie ta awaria, którą właśnie naprawiamy.
   */
  useEffect(() => {
    wyslij.current = (plik: File): void => {
      if (machineId === undefined || groupId === null) return;
      if (wysylka !== null) {
        setNota(t('pulpit.wysylkaWToku'));
        return;
      }
      setNota(null);
      setWysylka({ nazwa: plik.name, procent: 0 });
      void wyslijPlikNaMaszyne({
        groupId,
        machineId,
        plik,
        postep: (p) =>
          setWysylka({ nazwa: plik.name, procent: Math.round((p.wyslanoB / p.wszystkiegoB) * 100) }),
      })
        .then((w) => setNota(`${w.nazwa} → ${t('pulpit.naMaszynie')}`))
        .catch((e: unknown) => setNota(e instanceof Error ? e.message : t('pulpit.nieUdaloSie')))
        .finally(() => setWysylka(null));
    };
  }, [machineId, groupId, wysylka, t]);

  if (machineId === undefined || groupId === null) {
    return <p className="p-4 text-sm text-[var(--destructive)]">{t('pulpit.zlyAdres')}</p>;
  }

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  PROGRAMY SPS-a POD JEDNĄ POZYCJĄ (zamówione 16.09.2026: „możesz zgrupować te 3 przyciski
   *  w jedno rozwijane menu").
   *
   *  ⚠️ TO TRZY WARIANTY TEJ SAMEJ CZYNNOŚCI — „włącz albo wyłącz program SPS-a" — więc na pasku
   *  zajmowały trzy miejsca, mówiąc jedno. Reszta skrótów zostaje na wierzchu, bo każdy z nich
   *  robi co innego.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const programySps: { klucz: string; etykieta: string; ikona: typeof Wrench; akcja: () => Promise<unknown> }[] = [
    {
      klucz: 'spsui',
      etykieta: t('pulpit.skrot.spsui'),
      ikona: MonitorPlay,
      akcja: () => uruchomSpsui(machineId, groupId),
    },
    {
      klucz: 'utilities',
      etykieta: t('pulpit.skrot.utilities'),
      ikona: Wrench,
      akcja: () => uruchomSkrot(machineId, groupId, 'SPS_UTILITIES' satisfies SkrotMaszyny),
    },
    {
      klucz: 'raporty',
      etykieta: t('pulpit.skrot.raporty'),
      ikona: FileBarChart,
      akcja: () => uruchomSkrot(machineId, groupId, 'SPS_RAPORTY' satisfies SkrotMaszyny),
    },
  ];

  const skroty: { klucz: string; etykieta: string; ikona: typeof Wrench; akcja: () => Promise<unknown> }[] = [
    {
      klucz: 'pliki',
      etykieta: t('pulpit.skrot.pliki'),
      ikona: FolderOpen,
      akcja: () => uruchomSkrot(machineId, groupId, 'KATALOG_PRZESLANE' satisfies SkrotMaszyny),
    },
    {
      klucz: 'zadania',
      etykieta: t('pulpit.skrot.zadania'),
      ikona: ListTree,
      akcja: () => uruchomSkrot(machineId, groupId, 'MENEDZER_ZADAN' satisfies SkrotMaszyny),
    },
    {
      klucz: 'uruchom',
      etykieta: t('pulpit.skrot.uruchom'),
      ikona: TerminalSquare,
      akcja: () => uruchomSkrot(machineId, groupId, 'URUCHOM' satisfies SkrotMaszyny),
    },
  ];

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ZASILANIE — DWA KLIKNIĘCIA, I TO JEST WYMAGANIE, NIE NASZA OSTROŻNOŚĆ.
   *
   *  ⚠️⚠️ „Koniecznie z walidacją kliknięcia na obu" (15.09.2026). To jedyny przycisk w tym oknie,
   *  po którym automat NA PEWNO przestaje wydawać — reszta może go zepsuć przez przypadek, ten
   *  robi to z definicji.
   *
   *  ⚠️⚠️ WYŁĄCZENIE JEST NIEODWRACALNE ZDALNIE i dlatego ma inne zdanie w potwierdzeniu niż
   *  restart. Po `shutdown /s` maszyna nie wróci, dopóki ktoś fizycznie jej nie włączy — a automaty
   *  stoją u klienta, czasem za drabiną. Jedno zdanie dla obu ukrywałoby właśnie tę różnicę.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  BLOKADA WEJŚCIA I KONSERWACJA — stan trzymamy z ODPOWIEDZI SERWERA, nie z kliknięcia.
   *
   *  ⚠️⚠️ SERWER MOŻE ODDAĆ CO INNEGO, NIŻ KLIKNĘLIŚMY: konserwacja WYMUSZA blokadę, więc
   *  włączenie czarnego ekranu zapala też kłódkę. Przełącznik ustawiany „na wiarę" pokazywałby
   *  wtedy stan, którego na maszynie nie ma — a to jest dokładnie ta klasa kłamstwa, po której
   *  przestaje się wierzyć ekranom.
   *
   *  ⚠️ ODMOWA („sesja bez tunelu") wraca jako `ok: false` z powodem, a nie jako wyjątek —
   *  to normalny stan tego przycisku, a nie awaria.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const przeslijSterowanie = (chceBlokade: boolean, chceKonserwacje: boolean): void => {
    const s = sterowanie;
    if (s === null || machineId === undefined || groupId === null) return;
    zlec('sterowanie', async () => {
      const w = await sterujPulpitem(machineId, groupId, s.sesja, {
        blokada: chceBlokade,
        konserwacja: chceKonserwacje,
      });
      if (!w.ok) {
        setNota(w.powod ?? t('pulpit.nieUdaloSie'));
        return w;
      }
      setBlokada(w.blokada);
      setKonserwacja(w.konserwacja);
      return w;
    });
  };

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ZASILANIE PYTA OSOBNYM OKNEM — zgłoszone 16.09.2026: „muszą mieć jakieś lepsze zabezpieczenie
   *  i walidację, teraz wystarczy prosty dwuklik".
   *
   *  ⚠️⚠️ DWUKLIK W TO SAMO MIEJSCE BYŁ ZA SŁABY, bo nie wymagał ŻADNEJ zmiany celu: ręka, która
   *  raz trafiła w przycisk, trafia w niego drugi raz odruchowo. Teraz trzeba wejść w listę, wybrać
   *  pozycję i potwierdzić w oknie, w którym „Nie" jest domyślne — czyli trzy różne miejsca ekranu.
   *  To jedyny przycisk w tym oknie, po którym automat NA PEWNO przestaje wydawać towar.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const wykonajZasilanie = (co: ZasilanieMaszyny): void => {
    setPytanie(null);
    setMenuSystem(false);
    zlec('zasilanie', () => zlecZasilanie(machineId, groupId, co));
  };

  return (
    <div className="relative flex h-screen min-h-0 bg-black">
      <div className="min-w-0 flex-1">
        <PulpitMaszyny
          machineId={machineId}
          groupId={groupId}
          onMaszyna={setNazwa}
          onRozmiar={dopasujOkno}
          onSterowanie={setSterowanie}
          onSchowekZMaszyny={(tekst) => setNota(`${t('pulpit.schowekNaEkran')}: ${tekst.slice(0, 120)}`)}
        />
      </div>

      {/*
        ⚠️⚠️ SKRÓTY STOJĄ PIONOWO Z PRAWEJ — zamówione 15.09.2026: „by mieć z prawej strony od
        ekranu najpotrzebniejsze skróty". Pionowy pasek kosztuje SZEROKOŚĆ, której przy stojącym
        ekranie automatu jest w nadmiarze; ten sam zestaw u góry zabrałby wysokość, o którą
        w tym oknie toczy się cała walka.
      */}
      <aside
        className="flex shrink-0 flex-col items-center gap-1 border-l border-border bg-background py-2"
        style={{ width: PASEK_PX }}
        aria-label={t('pulpit.skroty')}
      >
        {/*
          ⚠️ IKONA TO NAPIS „SPS", a nie obrazek — zamówione wprost. Żadna ikona z zestawu nie mówi
          „programy SPS-a", a te trzy pozycje różnią się właśnie tym, że należą do SPS-a.
        */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuSps((b) => !b)}
            disabled={wToku !== null}
            title={t('pulpit.skrot.sps')}
            aria-label={t('pulpit.skrot.sps')}
            aria-expanded={menuSps}
            className={`flex w-14 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] disabled:opacity-40 ${
              menuSps ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {programySps.some((p) => p.klucz === wToku) ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <span className="text-[13px] font-bold leading-4 tracking-tight">SPS</span>
            )}
            <span className="truncate">{t('pulpit.skrot.sps')}</span>
          </button>

          {menuSps ? (
            <>
              {/* ⚠️ Kliknięcie obok zamyka listę — bez tego zostawałaby otwarta nad obrazem. */}
              <div className="fixed inset-0 z-40" onClick={() => setMenuSps(false)} />
              <div className="absolute right-full top-0 z-50 mr-1 w-44 overflow-hidden rounded-md border border-border bg-card shadow-lg">
                {programySps.map((p) => (
                  <button
                    key={p.klucz}
                    type="button"
                    onClick={() => {
                      setMenuSps(false);
                      zlec(p.klucz, p.akcja);
                    }}
                    disabled={wToku !== null}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-foreground hover:bg-muted disabled:opacity-40"
                  >
                    <p.ikona className="size-4" />
                    {p.etykieta}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {skroty.map((s) => (
          <button
            key={s.klucz}
            type="button"
            onClick={() => zlec(s.klucz, s.akcja)}
            disabled={wToku !== null}
            title={s.etykieta}
            aria-label={s.etykieta}
            className="flex w-14 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
          >
            {wToku === s.klucz ? <Loader2 className="size-4 animate-spin" /> : <s.ikona className="size-4" />}
            <span className="truncate">{s.etykieta}</span>
          </button>
        ))}

        {/*
          ⚠️⚠️ CTRL+ALT+DEL MA WŁASNY PRZYCISK, BO KLAWIATURĄ SIĘ TEGO NIE ZROBI. Kombinację
          przechwytuje system operatora i nigdy nie dociera do strony; po drugiej stronie tunelu
          serwer zamienia ją na `SendSAS`, co wolno wyłącznie procesowi systemowemu — czyli działa
          dopiero odkąd pomocnik chodzi jako SYSTEM w sesji konsoli.
        */}
        <button
          type="button"
          onClick={() => {
            const s = sterowanie;
            if (s === null || machineId === undefined || groupId === null) return;
            /**
             * ⚠️⚠️ NIE `s.ctrlAltDel()`, czyli NIE klawiszem przez noVNC. Zmierzone 16.09.2026:
             * pomocnik dostaje tę kombinację i nie potrafi jej wykonać („obie proby bez skutku,
             * pulpit nadal Default"), bo `SendSAS` z sesji konsoli nie przechodzi. Robi to agent.
             */
            zlec('ctrlAltDel', async () => {
              const w = await wyslijCtrlAltDel(machineId, groupId, s.sesja);
              setNota(w.ok ? t('pulpit.ctrlAltDelWyslane') : (w.powod ?? t('pulpit.nieUdaloSie')));
              return w;
            });
          }}
          disabled={sterowanie === null}
          title={t('pulpit.ctrlAltDel')}
          aria-label={t('pulpit.ctrlAltDel')}
          className="flex w-14 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        >
          <KeySquare className="size-4" />
          <span className="truncate">{t('pulpit.ctrlAltDel')}</span>
        </button>

        {/*
          ⚠️⚠️ DWA PRZEŁĄCZNIKI, NIE JEDEN. Blokada wejścia bywa potrzebna BEZ czarnego ekranu
          (człowiek przy maszynie ma widzieć, co robimy, ale nie klikać). Odwrotnie nie — czarny
          ekran ZAWSZE włącza blokadę, bo stan „nie widzi, ale klika" nie da się wytłumaczyć niczym.
        */}
        <button
          type="button"
          onClick={() => przeslijSterowanie(!blokada, konserwacja)}
          disabled={sterowanie === null}
          title={t('pulpit.blokada')}
          aria-label={t('pulpit.blokada')}
          aria-pressed={blokada}
          className={`flex w-14 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] disabled:opacity-40 ${
            blokada ? 'bg-[var(--warning)] text-black' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          {blokada ? <Lock className="size-4" /> : <LockOpen className="size-4" />}
          <span className="truncate">{t('pulpit.blokada')}</span>
        </button>

        <button
          type="button"
          onClick={() => przeslijSterowanie(blokada, !konserwacja)}
          disabled={sterowanie === null}
          title={t('pulpit.konserwacja')}
          aria-label={t('pulpit.konserwacja')}
          aria-pressed={konserwacja}
          className={`flex w-14 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] disabled:opacity-40 ${
            konserwacja ? 'bg-[var(--warning)] text-black' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          {konserwacja ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          <span className="truncate">{t('pulpit.konserwacja')}</span>
        </button>

        {/*
          ⚠️ JEDNA POZYCJA „SYSTEM", A W NIEJ LISTA — zgłoszone 16.09.2026. Restart i wyłączenie to
          dwa warianty tej samej, najgroźniejszej czynności; osobno na pasku kusiły kliknięciem.
        */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuSystem((b) => !b)}
            title={t('pulpit.system')}
            aria-label={t('pulpit.system')}
            aria-expanded={menuSystem}
            className={`flex w-14 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] ${
              menuSystem
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Power className="size-4" />
            <span className="truncate">{t('pulpit.system')}</span>
          </button>

          {menuSystem ? (
            <>
              {/* ⚠️ Kliknięcie obok zamyka listę — bez tego zostawałaby otwarta nad obrazem. */}
              <div className="fixed inset-0 z-40" onClick={() => setMenuSystem(false)} />
              <div className="absolute right-full top-0 z-50 mr-1 w-44 overflow-hidden rounded-md border border-border bg-card shadow-lg">
                {(['RESTART', 'WYLACZ'] as const).map((co) => (
                  <button
                    key={co}
                    type="button"
                    onClick={() => setPytanie(co)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-foreground hover:bg-muted"
                  >
                    {co === 'RESTART' ? <RotateCcw className="size-4" /> : <PowerOff className="size-4" />}
                    {t(co === 'RESTART' ? 'pulpit.restart' : 'pulpit.wylacz')}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>

        <button
          type="button"
          onClick={przelaczPelnyEkran}
          title={t('pulpit.pelnyEkran')}
          aria-label={t('pulpit.pelnyEkran')}
          className="mt-auto flex w-14 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {pelnyEkran ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          <span className="truncate">{t('pulpit.pelnyEkran')}</span>
        </button>

        {/*
          ⚠️⚠️ PASEK POSTĘPU JEST WARUNKIEM UŻYWALNOŚCI, a nie ozdobą. Przez łącze automatu 100 MB
          idzie kilka minut — bez licznika wysyłka wygląda dokładnie tak samo jak zawieszone okno,
          więc człowiek zamyka je i zaczyna od nowa. A wtedy naprawdę zaczyna od nowa.
        */}
        {wysylka !== null && (
          <div className="w-12 px-1 pb-1 text-center text-[10px] text-muted-foreground" role="status">
            <div className="h-1 w-full overflow-hidden rounded bg-muted">
              <div className="h-full bg-[var(--primary)]" style={{ width: `${wysylka.procent}%` }} />
            </div>
            <p className="mt-0.5 tabular-nums">{wysylka.procent}%</p>
          </div>
        )}

        {/* ⚠️ Odpowiedź na kliknięcie ląduje TU, a nie w okienku systemowym — pasek i tak jest na oczach. */}
        {nota !== null && wysylka === null && (
          <p className="px-1 pb-1 text-center text-[10px] leading-tight text-muted-foreground" role="status">
            {nota}
          </p>
        )}
      </aside>

      {/*
        ⚠️⚠️ OSOBNE OKNO POTWIERDZENIA, A NIE DRUGIE KLIKNIĘCIE W TEN SAM PRZYCISK. „Muszą mieć jakieś
        lepsze zabezpieczenie i walidację" (16.09.2026). Przycisk potwierdzający stoi w INNYM miejscu
        ekranu niż ten, który otworzył okno — odruchowy drugi klik trafia w pustkę, a nie w restart.

        ⚠️ „NIE" JEST PIERWSZE I MA FOKUS. Enter i spacja po otwarciu okna mają ANULOWAĆ, nie wykonać.
      */}
      {pytanie !== null && (
        <div
          className="absolute inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl">
            <p className="text-sm font-medium text-foreground">
              {t(pytanie === 'RESTART' ? 'pulpit.napewnoRestart' : 'pulpit.napewnoWylacz')}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{nazwa ?? ''}</p>

            {/*
              ⚠️⚠️ PO WYŁĄCZENIU MASZYNA NIE WSTANIE SAMA. Zdalnie nie da się jej włączyć — ktoś musi
              pojechać. Ostrzeżenie mówi WPROST, co trzeba wiedzieć PRZED kliknięciem, a nie po.
            */}
            {pytanie === 'WYLACZ' && (
              <p className="mt-3 rounded border border-[var(--destructive)] bg-[var(--destructive)]/10 p-2 text-xs font-medium text-[var(--destructive)]">
                {t('pulpit.wylaczOstrzezenie')}
              </p>
            )}

            {/*
              ⚠️⚠️ „NIE" I „TAK" NA PRZECIWNYCH KOŃCACH — zgłoszone 16.09.2026: „niech będą od siebie
              oddzielone". Obok siebie chybienie o kilka pikseli gasi automat; na przeciwnych końcach
              trzeba przejechać całą szerokość okna, czyli podjąć decyzję jeszcze raz.
            */}
            {/*
              ⚠️⚠️ „TAK" PO LEWEJ, „NIE" PO PRAWEJ — zgłoszone 16.09.2026: „ma być dalej myszką do tak".
              Pasek skrótów stoi przy PRAWEJ krawędzi, więc kursor przychodzi tu zawsze z prawej strony:
              to, co leży po prawej, jest pod ręką. Groźna odpowiedź ma być tą DALSZĄ.
            */}
            <div className="mt-4 flex items-center justify-between gap-8">
              <button
                type="button"
                onClick={() => {
                  /** ⚠️ Przy wyłączeniu pierwszy klik tylko ostrzega — wykonuje dopiero drugi. */
                  if (pytanie === 'WYLACZ' && !drugiKlik) {
                    setDrugiKlik(true);
                    return;
                  }
                  wykonajZasilanie(pytanie);
                }}
                className="rounded-md bg-[var(--destructive)] px-4 py-1.5 text-xs font-medium text-white hover:opacity-90"
              >
                {drugiKlik ? t('pulpit.klikniPonownie') : t('pulpit.tak')}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => setPytanie(null)}
                className="rounded-md bg-emerald-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              >
                {t('pulpit.nie')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
